/**
 * PolicyDocDashboard.tsx — theme-driven
 *
 * Policy-document coverage panel rendered inside DMTreeGridSummary's
 * "Policy Provenance" CollapsibleSection. Provides:
 *   • A pair of filter pills (policy-driven / LLM KB)
 *   • A stacked summary bar
 *   • A horizontal scroll row of per-document stat cards
 *
 * Filter behaviour:
 *   • Policy pill → onSourceFilter('policy'); toggles off → 'ALL'
 *   • LLM pill    → onSourceFilter('llm');    toggles off → 'ALL'
 *   • Doc card    → same as policy pill
 *
 * Also exports PolicyDashboardSummary — a compact summary chip-strip
 * for the parent CollapsibleSection's collapsed-state header.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faRobot, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { T, cls } from '../theme/theme';

export type SourceFilter = 'ALL' | 'policy' | 'llm';

type Rule = {
  id: string;
  policy_context?: { file_name: string; page: string; section: string; confidence: number } | null;
  result?: { status?: string };
};

type DocStat = {
  file_name: string; rule_count: number;
  pass: number; fail: number; other: number; avg_conf: number;
};

type Props = {
  allRules:           Rule[];
  activeSourceFilter: SourceFilter;
  onSourceFilter:     (v: SourceFilter) => void;
};

// ─── Stats computation ───────────────────────────────────────────────────────
function buildDocStats(rules: Rule[]): DocStat[] {
  const map:  Record<string, Omit<DocStat, 'avg_conf'>> = {};
  const conf: Record<string, { sum: number; n: number }> = {};
  rules.forEach(r => {
    const f = r.policy_context?.file_name; if (!f) return;
    if (!map[f])  map[f]  = { file_name: f, rule_count: 0, pass: 0, fail: 0, other: 0 };
    if (!conf[f]) conf[f] = { sum: 0, n: 0 };
    map[f].rule_count++;
    const s = r.result?.status?.toUpperCase();
    if (s === 'PASS') map[f].pass++;
    else if (s === 'FAIL') map[f].fail++;
    else map[f].other++;
    conf[f].sum += r.policy_context?.confidence ?? 0;
    conf[f].n++;
  });
  return Object.values(map)
    .map(d => ({
      ...d,
      avg_conf: conf[d.file_name]?.n
        ? Math.round((conf[d.file_name].sum / conf[d.file_name].n) * 100)
        : 0,
    }))
    .sort((a, b) => b.rule_count - a.rule_count);
}

// ─── Atoms ────────────────────────────────────────────────────────────────────
function MicroConf({ pct }: { pct: number }) {
  const c = pct >= 80 ? T.green : pct >= 60 ? T.amber : T.red;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div className="gw-mini-bar" style={{ flex: 1 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: c, borderRadius: 2 }} />
      </div>
      <span className="gw-mono" style={{
        fontSize: 9, fontWeight: 700, color: c, minWidth: 28,
      }}>
        {pct}%
      </span>
    </div>
  );
}

function PassFailBar({ pass, fail, other }: { pass: number; fail: number; other: number }) {
  const t = pass + fail + other;
  if (!t) return null;
  const p = (n: number) => `${Math.round((n / t) * 100)}%`;
  return (
    <div className="gw-stack-bar" style={{ marginTop: 6 }}>
      {pass  > 0 && <div style={{ width: p(pass),  background: T.green }}  title={`${pass} passed`} />}
      {fail  > 0 && <div style={{ width: p(fail),  background: T.red }}    title={`${fail} failed`} />}
      {other > 0 && <div style={{ width: p(other), background: T.border }} title={`${other} pending`} />}
    </div>
  );
}

function DocCard({
  stat, active, onClick,
}: {
  stat: DocStat; active: boolean; onClick: () => void;
}) {
  const short = stat.file_name.replace(/\.pdf$/i, '').replace(/_/g, ' ');
  return (
    <div onClick={onClick}
      style={{
        background: active ? T.brandLight : T.bgCard,
        border: `1.5px solid ${active ? T.brand : T.border}`,
        borderRadius: 10, padding: '11px 14px',
        minWidth: 200, flex: '0 0 auto',
        cursor: 'pointer',
        transition: 'border-color .15s, background .15s',
      }}>
      <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7, flexShrink: 0,
          background: active ? T.brand : T.brandLight,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background .15s',
        }}>
          <FontAwesomeIcon icon={faFileLines}
            style={{ fontSize: 13, color: active ? '#fff' : T.brand }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 700,
            color: active ? T.brand : T.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={stat.file_name}>
            {short}
          </div>
          <div className="gw-mono" style={{ fontSize: 9, color: T.textMuted, marginTop: 1 }}>
            {stat.rule_count} rule{stat.rule_count !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <PassFailBar pass={stat.pass} fail={stat.fail} other={stat.other} />

      <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
        {stat.pass > 0 && (
          <span className={cls('gw-badge', 'gw-badge-green')}>{stat.pass} pass</span>
        )}
        {stat.fail > 0 && (
          <span className={cls('gw-badge', 'gw-badge-red')}>{stat.fail} fail</span>
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="gw-label" style={{ marginBottom: 4 }}>Avg confidence</div>
        <MicroConf pct={stat.avg_conf} />
      </div>
    </div>
  );
}

// ─── Summary helper for the collapsible header ───────────────────────────────
/**
 * Compact at-a-glance summary rendered next to the section title in the
 * parent CollapsibleSection's header. Shows rule counts, doc count, and
 * an "active filter" hint when sourceFilter is non-default.
 */
export function PolicyDashboardSummary({
  allRules, activeSourceFilter,
}: {
  allRules: Rule[];
  activeSourceFilter: SourceFilter;
}) {
  if (!allRules || allRules.length === 0) return null;

  const policyRules = allRules.filter(r => !!r.policy_context?.file_name);
  const llmRules    = allRules.filter(r => !r.policy_context?.file_name);
  const docCount    = new Set(policyRules.map(r => r.policy_context!.file_name)).size;

  const filterLabel =
    activeSourceFilter === 'policy' ? 'POLICY ONLY' :
    activeSourceFilter === 'llm'    ? 'LLM ONLY' : null;

  return (
    <>
      {policyRules.length > 0 && (
        <>
          <span>
            <strong style={{ color: T.brand }}>{policyRules.length}</strong> from {docCount} doc{docCount !== 1 ? 's' : ''}
          </span>
          <span style={{ color: T.border }}>•</span>
        </>
      )}
      <span>
        <strong>{llmRules.length}</strong> LLM-derived
      </span>
      {filterLabel && (
        <span className="gw-mono" style={{
          fontSize: 9, fontWeight: 700,
          padding: '2px 7px', borderRadius: 8,
          background: T.brandLight, color: T.brand,
          marginLeft: 'auto',
        }}>
          {filterLabel}
        </span>
      )}
    </>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────
const PolicyDocDashboard: React.FC<Props> = ({
  allRules, activeSourceFilter, onSourceFilter,
}) => {
  const policyRules = allRules.filter(r => !!r.policy_context?.file_name);
  const llmRules    = allRules.filter(r => !r.policy_context?.file_name);
  const docStats    = buildDocStats(allRules);
  const total       = allRules.length;

  if (total === 0) return null;

  const pPct = total ? Math.round((policyRules.length / total) * 100) : 0;
  const lPct = 100 - pPct;

  const policyActive = activeSourceFilter === 'policy';
  const llmActive    = activeSourceFilter === 'llm';

  const togglePolicy = () => onSourceFilter(policyActive ? 'ALL' : 'policy');
  const toggleLlm    = () => onSourceFilter(llmActive    ? 'ALL' : 'llm');

  // Pill style — kept inline because the two pills have different brand
  // intentions (brand-blue vs slate-grey), and re-using .gw-pill would
  // require an extra color modifier.
  const pillBase: React.CSSProperties = {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10, fontWeight: 700,
    borderRadius: 8, padding: '4px 10px',
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 5,
    transition: 'background .15s, color .15s, border-color .15s',
    userSelect: 'none', border: '1.5px solid',
  };

  return (
    <div>
      {/* ── Filter pill row ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        flexWrap: 'wrap', marginBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 13, color: T.brand }} />
          <span className="gw-label">Policy coverage</span>
        </div>

        <button
          onClick={togglePolicy}
          title={policyActive ? 'Clear filter' : 'Show policy-driven rules only'}
          style={{
            ...pillBase,
            background:  policyActive ? T.brand      : T.brandLight,
            color:       policyActive ? '#fff'       : T.brand,
            borderColor: policyActive ? T.brand      : T.brandMid,
          }}>
          <FontAwesomeIcon icon={faFileLines} style={{ fontSize: 9 }} />
          {policyRules.length} policy-driven ({pPct}%)
          {policyActive && <span style={{ opacity: .7, fontSize: 9, marginLeft: 2 }}>✕</span>}
        </button>

        <button
          onClick={toggleLlm}
          title={llmActive ? 'Clear filter' : 'Show LLM knowledge-base rules only'}
          style={{
            ...pillBase,
            background:  llmActive ? T.textSecond : T.bgCard,
            color:       llmActive ? '#fff'        : T.textMuted,
            borderColor: llmActive ? T.textSecond  : T.border,
          }}>
          <FontAwesomeIcon icon={faRobot} style={{ fontSize: 9 }} />
          {llmRules.length} LLM KB ({lPct}%)
          {llmActive && <span style={{ opacity: .7, fontSize: 9, marginLeft: 2 }}>✕</span>}
        </button>

        {activeSourceFilter !== 'ALL' && (
          <span className="gw-mono" style={{
            fontSize: 9, color: T.textMuted, fontStyle: 'italic',
          }}>
            — filtered
          </span>
        )}

        {/* Right-aligned stacked bar */}
        <div style={{
          marginLeft: 'auto', width: 140, height: 8,
          borderRadius: 4, overflow: 'hidden', display: 'flex',
          border: `1px solid ${T.border}`,
        }}>
          {pPct > 0 && <div style={{ width: `${pPct}%`, background: T.brand }} />}
          {lPct > 0 && <div style={{ width: `${lPct}%`, background: T.bgSidebar }} />}
        </div>
      </div>

      {/* ── Cards row ── */}
      <div className="gw-scroll" style={{
        display: 'flex', gap: 12,
        overflowX: 'auto', paddingBottom: 4,
      }}>
        {docStats.map((stat, i) => (
          <DocCard
            key={i}
            stat={stat}
            active={policyActive}
            onClick={togglePolicy}
          />
        ))}

        {llmRules.length > 0 && (
          <div onClick={toggleLlm} style={{
            background:   llmActive ? `${T.textSecond}14` : T.bgSidebar,
            border:       `1.5px ${llmActive ? 'solid' : 'dashed'} ${llmActive ? T.textSecond : T.border}`,
            borderRadius: 10,
            padding: '11px 14px',
            minWidth: 170,
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color .15s, background .15s',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: llmActive ? T.textSecond : T.bgCard,
              border: `1px solid ${llmActive ? T.textSecond : T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .15s',
            }}>
              <FontAwesomeIcon icon={faRobot}
                style={{ fontSize: 13, color: llmActive ? '#fff' : T.textMuted }}
              />
            </div>
            <div className="gw-mono" style={{
              fontSize: 11, fontWeight: 700,
              color: llmActive ? T.textSecond : T.textMuted,
            }}>
              LLM Knowledge Base
            </div>
            <div className="gw-mono" style={{ fontSize: 9, color: T.textMuted }}>
              {llmRules.length} rule{llmRules.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyDocDashboard;